import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"

interface AdminTripCard {
  title: string
  dateRangeText: string
  editHref: string
}

interface AdminDayRow {
  date: string
  shortDate: string
  title: string
  itemsLabel: string
  isEmpty: boolean
  href: string
}

interface AdminDaysProps {
  trip: AdminTripCard
  days: AdminDayRow[]
  labels: { tripSection: string; daysSection: string; editTrip: string }
}

const ROW_CLASSES = "flex items-center gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-center gap-3 py-3"

// The admin area's root screen (t009, `Schedule::AdminDaysController#index`):
// the trip card and every date in the trip's range, in date order.
// Implements docs/design/admin.html's "State 1" verbatim (AC-1, AC-6,
// AC-13). The day list comes from the controller's own per-date
// derivation (a date with no `Day` row yet still gets a row), not from
// `Day` records alone — unlike the public main page's `Days.tsx`, which
// renders only existing records.
//
// The last row drops its bottom border (matching `Day.tsx`'s own
// isLast treatment of its timeline rows) rather than doubling it against
// the section's own edge — the design's two-item sample does not
// disambiguate this, since neither shown row is marked as the list's own
// last one.
export default function AdminDays({ trip, days, labels }: AdminDaysProps) {
  return (
    <AdminLayout mainClassName="flex flex-col gap-6 p-4">
      <section className="flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {labels.tripSection}
        </h2>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3.5 py-3">
          <div className="flex flex-col gap-0.5">
            <div className="text-[15px] font-semibold leading-[22px] text-gray-900">{trip.title}</div>
            <div className="text-[13px] leading-[18px] text-gray-500">{trip.dateRangeText}</div>
          </div>
          <Link href={trip.editHref} className="text-[13px] font-medium leading-[18px] text-accent">
            {labels.editTrip}
          </Link>
        </div>
      </section>
      <section className="flex flex-col gap-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {labels.daysSection}
        </h2>
        <ol className="flex flex-col">
          {days.map((day, index) => (
            <li key={day.date}>
              <Link
                href={day.href}
                className={index === days.length - 1 ? LAST_ROW_CLASSES : ROW_CLASSES}
              >
                <div className="w-14 shrink-0 text-[13px] leading-[18px] text-gray-500 tabular-nums">
                  {day.shortDate}
                </div>
                <div className="flex grow flex-col gap-px">
                  <div className="text-[15px] font-semibold leading-[22px] text-gray-900">
                    {day.title}
                  </div>
                  <div
                    className={`text-[13px] leading-[18px] ${day.isEmpty ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {day.itemsLabel}
                  </div>
                </div>
                <svg
                  aria-hidden="true"
                  className="text-gray-400"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M6 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </li>
          ))}
        </ol>
      </section>
    </AdminLayout>
  )
}
